// Pure, framework-free matching logic — ported from the former compute_timestamps
// edge function (retired in v1.0.5). Runs in the browser, unit-testable in Node.

export interface Cue {
  start: number;
  end: number;
  text: string;
}
export interface Window {
  start: number;
  text: string;
}
export interface TaskLike {
  // Tasks come from a JSONB column, so `text` is loosely typed; matchers String()-coerce it.
  text?: unknown;
}

export function parseTimestamp(ts: string): number {
  const parts = ts.split(":");
  if (parts.length === 3)
    return +parts[0] * 3600 + +parts[1] * 60 + parseFloat(parts[2]);
  return +parts[0] * 60 + parseFloat(parts[1]);
}

export function parseVtt(text: string): Cue[] {
  const cues: Cue[] = [];
  // Zoom VTT uses CRLF — normalize so blank-line block separators split correctly.
  for (const block of text.replace(/\r\n?/g, "\n").split("\n\n")) {
    const lines = block.trim().split("\n");
    const timeLine = lines.find((l) => l.includes("-->"));
    if (!timeLine) continue;
    const [startStr, endStr] = timeLine.split("-->").map((s) => s.trim());
    const textLines = lines.slice(lines.indexOf(timeLine) + 1);
    cues.push({
      start: parseTimestamp(startStr),
      end: parseTimestamp(endStr),
      text: textLines.join(" ").trim(),
    });
  }
  return cues;
}

export function buildWindows(cues: Cue[], size = 2): Window[] {
  return cues.map((_, i) => {
    const slice = cues.slice(i, i + size);
    return { start: slice[0].start, text: slice.map((c) => c.text).join(" ") };
  });
}

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .split(/\s+/)
    .filter(Boolean);
}

export function bm25Scores(query: string, windows: Window[]): number[] {
  const k1 = 1.2;
  const b = 0.75;
  const queryTokens = tokenize(query);
  const docs = windows.map((w) => tokenize(w.text));
  const avgDl = docs.reduce((s, d) => s + d.length, 0) / (docs.length || 1);
  const N = docs.length;
  const idf: Record<string, number> = {};
  for (const term of queryTokens) {
    const df = docs.filter((d) => d.includes(term)).length;
    idf[term] = Math.log((N - df + 0.5) / (df + 0.5) + 1);
  }
  return docs.map((doc) => {
    let score = 0;
    const dl = doc.length;
    for (const term of queryTokens) {
      const tf = doc.filter((t) => t === term).length;
      score +=
        (idf[term] ?? 0) *
        ((tf * (k1 + 1)) / (tf + k1 * (1 - b + (b * dl) / avgDl)));
    }
    return score;
  });
}

export function cosineSimilarity(a: number[], b: number[]): number {
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    na += a[i] * a[i];
    nb += b[i] * b[i];
  }
  const denom = Math.sqrt(na) * Math.sqrt(nb);
  return denom === 0 ? 0 : dot / denom;
}

export function reciprocalRankFusion(
  rankedLists: number[][],
  k = 60,
): number[] {
  const scores = new Array(rankedLists[0].length).fill(0);
  for (const ranks of rankedLists) {
    const sorted = ranks
      .map((score, idx) => ({ score, idx }))
      .sort((a, b) => b.score - a.score);
    for (let rank = 0; rank < sorted.length; rank++) {
      scores[sorted[rank].idx] += 1 / (k + rank + 1);
    }
  }
  return scores;
}

const SILENCE_GAP_SECONDS = 3;
// Small lookback: snap a mid-paragraph match back to a *recent* pause only.
// A large window here collapsed matches to the meeting's first pause on
// continuous-speech transcripts; tuned on real meeting data.
const MAX_LOOKBACK_SECONDS = 8;

export function findParagraphStart(cues: Cue[], matchedStart: number): number {
  const matchIdx = cues.findIndex((c) => c.start >= matchedStart);
  if (matchIdx <= 0) return matchedStart;
  const earliest = matchedStart - MAX_LOOKBACK_SECONDS;
  for (let i = matchIdx - 1; i > 0; i--) {
    if (cues[i].start < earliest) break;
    if (cues[i].start - cues[i - 1].end >= SILENCE_GAP_SECONDS)
      return cues[i].start;
  }
  return matchedStart;
}

function bestIndex(scores: number[]): number {
  let best = -1;
  let bestScore = -Infinity;
  for (let i = 0; i < scores.length; i++) {
    if (scores[i] > bestScore) {
      bestScore = scores[i];
      best = i;
    }
  }
  return best;
}

/** Instant, no model: BM25 best window + paragraph snap. The always-on first-paint tier. */
export function bm25Offsets(
  tasks: TaskLike[],
  cues: Cue[],
  windows: Window[],
): (number | null)[] {
  if (!windows.length) return tasks.map(() => null);
  return tasks.map((t) => {
    const text = String(t.text ?? "");
    if (!text) return null;
    const scores = bm25Scores(text, windows);
    const best = bestIndex(scores);
    if (best < 0 || scores[best] <= 0) return null;
    return Math.round(findParagraphStart(cues, windows[best].start));
  });
}

/** Full semantic: fuse BM25 + cosine over ALL windows. taskEmbeddings/windowEmbeddings aligned to tasks/windows. */
export function semanticOffsets(
  tasks: TaskLike[],
  cues: Cue[],
  windows: Window[],
  taskEmbeddings: number[][],
  windowEmbeddings: number[][],
): (number | null)[] {
  if (!windows.length) return tasks.map(() => null);
  return tasks.map((t, ti) => {
    const text = String(t.text ?? "");
    if (!text) return null;
    const taskEmb = taskEmbeddings[ti];
    if (!taskEmb) return null;
    const bm25 = bm25Scores(text, windows);
    const emb = windowEmbeddings.map((w) => cosineSimilarity(taskEmb, w));
    const rrf = reciprocalRankFusion([bm25, emb]);
    const best = bestIndex(rrf);
    if (best < 0) return null;
    return Math.round(findParagraphStart(cues, windows[best].start));
  });
}
